/**
 * A project's home (07 Workshop, rows 2 and 58): its name in Crake, what it is
 * for, Share (the owner's), a composer to start one more chat, and its chats
 * by last activity, each with the last line of M7's reply. A member sees who
 * shared it with them instead of Share. The project panel sits beside it.
 */
import { useState } from "react";
import type { WorkshopQuickCreateRef } from "@/types/workshop.type";
import { Button, Menu, MenuItem, MenuTrigger, Popover, type Key } from "react-aria-components";
import { ArrowDownUp, Check, MessageSquare, Users } from "lucide-react";
import { useCoShellPortal } from "@/app/routes/dashboard/CoShellPortalContext";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import type { WorkshopSession } from "@/types/workshop.type";
import type { WorkshopProjectDetail } from "../api/useWorkshopProjects";
import { useWorkshopSessions } from "../api/useWorkshopSessions";
import { PROJECT_SORTS, type ProjectSort } from "../workshopSort";
import { WsInlineError } from "./WorkshopParts";
import { heldThreadKey, threadTitle } from "./WorkshopSidebar";
import WorkshopStartComposer, { type WorkshopBootstrapPayload } from "./WorkshopStartComposer";
import { useWorkshopUndo } from "./WorkshopUndo";
import { useComposerVoice } from "../../agent/rail/useComposerVoice";

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "SEP 24 · 15:10" */
export function chatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const day = date.toLocaleDateString(undefined, { month: "short", day: "numeric" }).toUpperCase();
  const time = date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${day} · ${time}`;
}

const time = (iso: string | null | undefined) => {
  const t = iso ? new Date(iso).getTime() : 0;
  return Number.isNaN(t) ? 0 : t;
};

function sortChats(chats: WorkshopSession[], sort: ProjectSort): WorkshopSession[] {
  const list = [...chats];
  switch (sort) {
    case "newest":
      return list.sort((a, b) => time(b.createdAt) - time(a.createdAt));
    case "oldest":
      return list.sort((a, b) => time(a.createdAt) - time(b.createdAt));
    case "name":
      return list.sort((a, b) => threadTitle(a).localeCompare(threadTitle(b), undefined, { sensitivity: "base" }));
    default:
      return list.sort((a, b) => time(b.lastMessageAt || b.createdAt) - time(a.lastMessageAt || a.createdAt));
  }
}

type Props = {
  detail: WorkshopProjectDetail;
  canWrite: boolean;
  initialText?: string;
  onStart: (payload: WorkshopBootstrapPayload) => Promise<void>;
  onStartWithFiles: (files: File[]) => void;
  onOpenThread: (threadId: string) => void;
  onShare: () => void;
};

export default function WorkshopProjectHome({
  detail,
  canWrite,
  initialText = "",
  onStart,
  onStartWithFiles,
  onOpenThread,
  onShare,
}: Props) {
  const { project } = detail;
  const portal = useCoShellPortal();
  const undo = useWorkshopUndo();
  const { sessions, isLoading, isError, refetch } = useWorkshopSessions(project.id);
  const [text, setText] = useState(initialText);
  const voice = useComposerVoice(project.brandId, setText);
  const [pills, setPills] = useState<WorkshopQuickCreateRef[]>([]);
  const [sort, setSort] = useState<ProjectSort>("activity");
  const chats = sortChats(
    sessions.filter((s) => !undo.isHeld(heldThreadKey(s.id))),
    sort,
  );
  const archived = project.status === "archived";
  const people = 1 + (project.members?.length ?? 0);

  return (
    <div className="ws-page ws-page--project">
      <header className="ws-page-head">
        <div className="ws-page-title-row">
          <h1 className="ws-page-title">{project.name}</h1>
          {project.isOwner && !archived ? (
            <div className="ws-page-actions">
              <KitButton variant="secondary" iconLeft={Users} onClick={onShare}>
                Share
              </KitButton>
            </div>
          ) : null}
        </div>
        {project.description ? <p className="ws-page-sub ws-page-sub--desc">{project.description}</p> : null}
      </header>

      <div className="ws-page-body ws-page-body--project">
        {project.isOwner === false ? (
          <p className="ws-shared-with">
            Shared with you by {project.owner?.name?.trim() || "a teammate"} · {plural(people, "person", "people")}
          </p>
        ) : null}
        {canWrite && !archived ? (
          <WorkshopStartComposer
            voice={voice}
            text={text}
            onTextChange={setText}
            quickCreates={pills}
            onQuickCreatesChange={setPills}
            placeholder="Start a chat in this project"
            inputLabel={`Start a chat in ${project.name}`}
            focusSeq={initialText ? 1 : 0}
            onStart={onStart}
            onAttachFiles={onStartWithFiles}
          />
        ) : null}

        <div className="ws-section-head">
          <span className="ws-section-label">Chats</span>
          {isLoading ? null : <span className="ws-section-count">{chats.length}</span>}
          <span className="ws-side-push" />
          <MenuTrigger>
            <Button className="ws-side-icon ws-side-icon--sm" aria-label="Sort chats">
              <ArrowDownUp aria-hidden="true" />
            </Button>
            <Popover
              placement="bottom end"
              offset={6}
              UNSTABLE_portalContainer={portal ?? undefined}
              className="ws-menu-pop ws-menu-pop--sort"
            >
              <Menu
                aria-label="Sort chats"
                className="ws-menu-list"
                selectionMode="single"
                selectedKeys={[sort]}
                onAction={(key: Key) => setSort(key as ProjectSort)}
              >
                {PROJECT_SORTS.map((s) => (
                  <MenuItem key={s.id} id={s.id} textValue={s.label} className="ws-menu-row">
                    {({ isSelected }) => (
                      <>
                        <span className="ws-menu-row-label">{s.label}</span>
                        {isSelected ? <Check className="ws-menu-row-check" aria-hidden="true" /> : null}
                      </>
                    )}
                  </MenuItem>
                ))}
              </Menu>
            </Popover>
          </MenuTrigger>
        </div>

        {isLoading ? (
          <ul className="ws-chat-list" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <li key={i} className="ws-chat-item ws-chat-item--skel">
                <span className="ws-skel ws-skel--line" style={{ width: `${62 - i * 12}%` }} />
                <span className="ws-skel ws-skel--line" style={{ width: `${44 - i * 6}%` }} />
              </li>
            ))}
          </ul>
        ) : isError && chats.length === 0 ? (
          <WsInlineError title="Couldn't load the chats." onRetry={() => void refetch()} />
        ) : chats.length === 0 ? (
          <p className="ws-page-empty">No chats yet. Start one above; Creo reads this project’s instructions and files.</p>
        ) : (
          <ul className="ws-chat-list">
            {chats.map((session) => {
              const Icon = session.sharedAt ? Users : MessageSquare;
              return (
                <li key={session.id}>
                  <button type="button" className="ws-chat-item" onClick={() => onOpenThread(session.id)}>
                    <Icon className="ws-chat-item-icon" aria-hidden="true" />
                    <span className="ws-chat-item-text">
                      <span className="ws-chat-item-title">{threadTitle(session)}</span>
                      {session.lastReplyPreview ? (
                        <span className="ws-chat-item-snippet">Creo: {session.lastReplyPreview}</span>
                      ) : null}
                    </span>
                    <span className="ws-chat-item-when">{chatWhen(session.lastMessageAt || session.createdAt)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

